import { useState, useEffect } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";
import { Mic, MicOff, Pause, Play, Clock, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { GroundingFiles } from "@/components/ui/grounding-files";
import GroundingFileView from "@/components/ui/grounding-file-view";
import StatusMessage from "@/components/ui/status-message";
import Animation from "@/components/ui/animation"
import ChatHistory from "@/components/ui/chatHistory";
import TopicList from "@/components/ui/topicList";
import TopicButton from "@/components/ui/topic-button";
import ChatbotPage from "./pages/ChatbotPage";
import ConversationHistoryPage from "./pages/ConversationHistoryPage";
import SsoCallbackPage from "./pages/SsoCallbackPage";
import PostLogoutRedirectPage from "./pages/PostLogoutRedirectPage";
import NoPermissionPage from "./pages/NoPermissionPage";
import Navigation from "./components/Navigation";
import RequireAuth from "./components/RequireAuth";
import { isSsoEnforced, SSO_POST_LOGOUT_PATH, TOPIC_NAV_SKIP_AUTH_PROFILE_KEY } from "@/config/sso";
import { getStoredAccessToken, getUserDetails, getUserEmailFromProfile } from "@/services/ssoApi";
import {
    CHAT_AUDIO_LISTEN_GRADIENT,
    CHAT_AUDIO_PAUSE,
    CHAT_AUDIO_PLAY,
    CHAT_MUTED_CORAL,
    APP_PAGE_BACKGROUND_STYLE,
    GRADIENT_TITLE_STYLE,
    SIDE_PANEL_CLASS,
    SIDE_PANEL_INNER_CLASS,
} from "@/lib/chatTheme";

import useRealTime from "@/hooks/useRealtime";
import useAudioRecorder from "@/hooks/useAudioRecorder";
import useAudioPlayer from "@/hooks/useAudioPlayer";

import { GroundingFile, ToolResult, ChatHistoryItem, TopicItem, type ThinkingBannerKind } from "./types";

import { TOPIC_ITEMS } from "@/lib/topicConfig";

import logo from "./assets/Beluga 1.png";
import "./App.css";

function VoicePage() {
    const [isRecording, setIsRecording] = useState(false);
    
    console.log('VoicePage rendered');
    const [isMicOff, setIsMicOff] = useState<Boolean>(false);
    const [groundingFiles, setGroundingFiles] = useState<GroundingFile[]>([]);
    const [selectedFile, setSelectedFile] = useState<GroundingFile | null>(null);
    const [mouthOpen, setMouthOpen] = useState(0);
    const [isStopAnimation, setIsStopAnimation] = useState(true);
    const [isThinking, setIsThinking] = useState(false);
    const [thinkingBanner, setThinkingBanner] = useState<ThinkingBannerKind>("thinking");
    const [showChatHistory, setShowChatHistory] = useState(false);
    const [showTopicList, setShowTopicList] = useState(false);
    const [topicList, setTopicList] = useState<TopicItem[]>([]);
    const [selectedTopic, setSelectedTopic] = useState<string | null>(null);
    const [chatHistoryData, setChatHistoryData] = useState<ChatHistoryItem[]>([]);
    const [newChatHistory, setNewChatHistory] = useState('');
    const [ssoUserEmail, setSsoUserEmail] = useState<string | null>(null);

    useEffect(() => {
        let cancelled = false;
        void (async () => {
            const token = getStoredAccessToken();
            const details = await getUserDetails(token);
            if (cancelled) return;
            if (details && !("error" in details)) {
                const em = getUserEmailFromProfile(details);
                setSsoUserEmail(em ? em.trim().toLowerCase() : null);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, []);

    // Monitor selectedTopic changes
    useEffect(() => {
        console.log('=== selectedTopic State Change ===');
        console.log('selectedTopic updated to:', selectedTopic);
        console.log('================================');
    }, [selectedTopic]);

    // Initialize topic list and URL parsing on mount
    useEffect(() => {
        setTopicList(TOPIC_ITEMS);
    }, []); // Empty dependency array - runs only once on mount

    // Handle URL topic parsing and normalization
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const topic = params.get('topic');
        console.log('=== URL Topic Extraction Debug ===');
        console.log('Current URL:', window.location.href);
        console.log('Search params:', window.location.search);
        console.log('Extracted topic:', topic);
        console.log('Topic type:', typeof topic);
        console.log('================================');
        
        // Normalize topic to lowercase underscore format
        let normalizedTopic = topic;
        if (topic) {
            // Convert common uppercase formats to lowercase underscore
            const topicMapping: { [key: string]: string } = {
                'FUTURE+MOBILITY': 'future_mobility',
                'FUTURE MOBILITY': 'future_mobility',
                'GREEN+LIVING': 'green_living',
                'GREEN LIVING': 'green_living',
                'SUSTAINABLE+PACKAGING': 'sustainable_packaging',
                'SUSTAINABLE PACKAGING': 'sustainable_packaging',
                'BETTER+CONSTRUCTION+AND+INFRASTRUCTURE': 'better_construction_and_infrastructure',
                'BETTER CONSTRUCTION AND INFRASTRUCTURE': 'better_construction_and_infrastructure',
                'RESOURCE+EFFICIENCY+FACTORY': 'resource_efficiency_factory',
                'RESOURCE EFFICIENCY FACTORY': 'resource_efficiency_factory',
                'HEALTH+&+WELL-BEING': 'health_well_being',
                'HEALTH & WELL-BEING': 'health_well_being',
                'CLOSING+THE+LOOP': 'closing_the_loop',
                'CLOSING THE LOOP': 'closing_the_loop',
                'NEED+ZONE': 'need_zone',
                'NEED ZONE': 'need_zone',
                'POLICY': 'policy',
                'SCGC+INFORMATION': 'scgc_info',
                'SCGC INFORMATION': 'scgc_info',
                'SCGC+INFO': 'scgc_info',
                'SCGC INFO': 'scgc_info',
            };
            
            normalizedTopic = topicMapping[topic] || topic.toLowerCase().replace(/[+ ]/g, '_');
            
            // If topic was normalized, update the URL
            if (normalizedTopic !== topic) {
                console.log('Normalizing topic from', topic, 'to', normalizedTopic);
                const newParams = new URLSearchParams(window.location.search);
                newParams.set('topic', normalizedTopic);
                const newUrl = `${window.location.pathname}?${newParams.toString()}`;
                window.history.replaceState({}, '', newUrl);
            }
        }
        
        setSelectedTopic(normalizedTopic);
    }, []); // Empty dependency array - runs only once on mount

    // Handle keyboard events
    useEffect(() => {
        window.addEventListener('keydown', handleKeyDown);
        return () => {
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, [isMicOff]); // Include isMicOff in dependencies since handleKeyDown uses it

    // Start WebSocket connection when page loads
    useEffect(() => {
        console.log('=== Auto-starting WebSocket connection ===');
        console.log('Page loaded, starting WebSocket session...');
        console.log('Starting with clean URL (no topic parameter)');
        console.log('==========================================');
        
        // Start WebSocket connection with clean URL (no topic parameter)
        startSessionBase(undefined);
        
        return () => {
            console.log('=== Cleaning up WebSocket connection ===');
            console.log('Page unloaded, WebSocket will be closed');
            console.log('========================================');
        }
    }, []) // Empty dependency array means this runs once on mount

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.code === 'Space') {
        event.preventDefault();
        setIsMicOff(!isMicOff)
      }
    };

    const { startSession: startSessionBase, addUserAudio, inputAudioBufferClear, sendJsonMessage } = useRealTime({
        userEmail: ssoUserEmail,
        onWebSocketOpen: () => console.log("WebSocket connection opened"),
        onWebSocketClose: () => console.log("WebSocket connection closed"),
        onWebSocketError: event => console.error("WebSocket error:", event),
        onReceivedError: message => {
            console.error("error", message);
            setIsThinking(false);
            setThinkingBanner("thinking");
        },
        onReceivedResponseAudioDelta: async message => {
            // Only clear when there is actual PCM payload. Empty/stray deltas can arrive before tool
            // search completes and would hide the thinking state for the whole turn.
            if (message.delta && message.delta.length > 0) {
                setIsThinking(false);
                setThinkingBanner("thinking");
            }

            const mouthOpen = await pcmToMouthOpen(message.delta)
            setMouthOpen(mouthOpen)

            void playAudio(message.delta);
        },
        onReceivedInputAudioBufferSpeechStarted: () => {
            stopAudioPlayer();
        },
        onReceivedExtensionMiddleTierToolResponse: message => {            
            const result: ToolResult = JSON.parse(message.tool_result);

            // const files: GroundingFile[] = result.sources.map(x => {
            //     return { id: x.chunk_id, name: x.title, content: x.chunk };
            // });
            const files: GroundingFile[] = result.sources.map(x => {
                return { id: '0', name: x.fileName, content: x.text };
            });

            setGroundingFiles(prev => [...prev, ...files]);
        },
        setIsThinking,
        setThinkingBanner,
        setTextChatHistory: (id: string, text: string , type: string, replace = false) => {
            const tempChatHistoryData: ChatHistoryItem[] = chatHistoryData
            const indexItem = tempChatHistoryData.findIndex(item=> item.id === id)
            if (indexItem === -1) {
                const now = new Date();
                const hours = String(now.getHours()).padStart(2, '0');
                const minutes = String(now.getMinutes()).padStart(2, '0');
                const time = `${hours}:${minutes}`;

                tempChatHistoryData.push({id, text, type, time})
                setChatHistoryData(tempChatHistoryData)
            } else {
                tempChatHistoryData[indexItem].text = replace ? text : (tempChatHistoryData[indexItem].text + text)
                setChatHistoryData(tempChatHistoryData)
            }
            setNewChatHistory(text)
        }
    });

    const { reset: resetAudioPlayer, play: playAudio, stop: stopAudioPlayer } = useAudioPlayer();
    const { start: startAudioRecording, stop: stopAudioRecording } = useAudioRecorder({ onAudioRecorded: addUserAudio });

    const onToggleListening = async () => {
        if (!isRecording) {
            setIsStopAnimation(false)
            startSession();
            await startAudioRecording();
            await resetAudioPlayer();
            if (isMicOff) {
                await stopAudioRecording();
            }
            setIsRecording(true);
        } else {
            setIsStopAnimation(true)
            await stopAudioRecording();
            stopAudioPlayer();
            inputAudioBufferClear();

            setIsRecording(false);
        }
    };

    const handelSetMicOff = async () => {
        setIsThinking(false)
        setThinkingBanner("thinking")
        if (isMicOff) {
            setIsMicOff(false)
            await startAudioRecording();
            console.log('turn on microphone');
        } else {
            setIsMicOff(true)
            await stopAudioRecording();
            console.log('turn off microphone');
        }
    }

    // Wrapper function to start session with topic
    const startSession = async () => {
        console.log('=== startSession Debug ===');
        console.log('selectedTopic state:', selectedTopic);
        console.log('selectedTopic type:', typeof selectedTopic);
        console.log('Passing to startSessionBase:', selectedTopic || undefined);
        console.log('========================');
        await startSessionBase(selectedTopic || undefined);
    };

    const { t } = useTranslation();

    const pcmToMouthOpen = async (base64Audio: string) => {
        if (!base64Audio) return 0;

        // 1. Decode base64 → Uint8Array (PCM 16-bit LE)
        const binary = atob(base64Audio);
        const bytes = Uint8Array.from(binary, c => c.charCodeAt(0));
        const pcm = new Int16Array(bytes.buffer); // raw PCM

        // 2. คำนวณ RMS → mouthOpen
        let sum = 0;
        for (let i = 0; i < pcm.length; i++) {
            const normalized = pcm[i] / 32768;
            sum += normalized * normalized;
        }

        const rms = Math.sqrt(sum / pcm.length);
        const gain = 4; // ปรับความไว
        const mouthOpen = Math.min(1, rms * gain);

        return mouthOpen;
    }

    const handleClickShowChatHistory = async () => {
        setShowChatHistory(!showChatHistory)
        setShowTopicList(false)
    }

    const handleClickShowTopicList = async () => {
        setShowTopicList(!showTopicList)
        setShowChatHistory(false)
    }
    
    const handleClickSelectTopic = async (key: string) => {
        const normalizedKey = key.toLowerCase().replace(/[+ ]/g, "_");
        if (isSsoEnforced()) {
            sessionStorage.setItem(TOPIC_NAV_SKIP_AUTH_PROFILE_KEY, "1");
        }
        const params = new URLSearchParams(window.location.search);
        params.set("topic", normalizedKey);
        const qs = params.toString();
        window.location.assign(qs ? `${window.location.pathname}?${qs}` : window.location.pathname);
    };

    const handleClickCancelSelectTopic = async () => { 
        console.log('=== Topic Cancellation Debug ===');
        console.log('Cancelling topic selection');
        console.log('WebSocket available:', !!sendJsonMessage);
        console.log('================================');
        
        // Update URL
        const params = new URLSearchParams(window.location.search);
        params.delete('topic');
        window.history.replaceState({}, '', `${window.location.pathname}?${params.toString()}`);
        
        // Update state
        setSelectedTopic(null);
        
        // Send topic removal to backend immediately via WebSocket
        if (sendJsonMessage) {
            try {
                const topicMessage = {
                    type: "session.update",
                    session: {
                        type: "realtime" as const,
                        topic: null
                    }
                };
                console.log('Sending topic removal to backend:', topicMessage);
                sendJsonMessage(topicMessage);
            } catch (error) {
                console.error('Failed to send topic removal to backend:', error);
            }
        } else {
            console.warn('WebSocket not available, topic removal will be sent when microphone is opened');
        }
    }

    return (
        <div
            className="flex min-h-0 min-h-screen min-h-[100dvh] w-full min-w-0 items-stretch"
            style={APP_PAGE_BACKGROUND_STYLE}
        >

            { 
                (showChatHistory || showTopicList) && (
                    <X
                        onClick={showChatHistory ? handleClickShowChatHistory : handleClickShowTopicList}
                        className="h-5 w-5 text-white-500"
                        style={{
                            zIndex: '99',
                            color: '#1A1A1A',
                            cursor: 'pointer',
                            position: 'absolute',
                            top: 30,
                            right: 30,
                        }}
                    />
                ) 
            }

            <div 
                className="flex min-h-0 min-h-screen min-h-[100dvh] min-w-0 flex-1 flex-col text-gray-900"
                style={{
                    width: '100%',
                    position: 'relative',
                }}
            > 
                <div
                    className={(showChatHistory || showTopicList) ? 'hidden md:flex' : 'flex'}
                    style={{
                        // display: 'flex',
                        alignItems: 'center',
                        zIndex: '99',
                        position: 'absolute',
                        top: 20,
                        right: 30,
                        maxWidth: 'min(92vw, 28rem)',
                    }}
                >
                    <TopicButton
                        selectedTopic={selectedTopic}
                        topicList={topicList}
                        onOpen={handleClickShowTopicList}
                        onClear={handleClickCancelSelectTopic}
                        className="max-w-[min(70vw,20rem)]"
                    />

                    <div style={{width: '2px', height: '25px', border: '1px solid #D1D1D1', margin: '0px 20px'}}> </div>
                    <Clock
                        onClick={handleClickShowChatHistory}
                        className="h-5 w-5 text-white-500"
                        style={{
                            color: '#1A1A1A',
                            cursor: 'pointer',
                        }}
                    />
                </div>

                <main className="flex min-h-0 min-w-0 flex-1 flex-grow flex-col items-center justify-center">
                    <div
                        style={{
                            marginTop: '3vh',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '12px',
                        }}
                    >
                        <img src={logo} alt="Channel logo" className="h-[6em] md:h-[7em] lg:h-[8em] hidden sm:block" />
                        <img src={logo} alt="Channel logo" className="h-[4em] block sm:hidden" style={{position: 'absolute', top:'5px', left:'5px'}} />
                        <h1 
                            className={`text-blue-600 font-bold ${ (showChatHistory || showTopicList) ? 'mt-[0em]' : 'mt-[2em]'} sm:mt-1.5 text-[2em] sm:text-[2.5em] md:text-[3em] lg:text-[4em]`}
                            style={GRADIENT_TITLE_STYLE}
                        >
                            {t("app.title")}
                        </h1>
                    </div>
                    <div 
                        className={`${(showChatHistory || showTopicList) ? 'hidden sm:flex' : 'flex'} min-h-0 min-w-0 w-full flex-grow flex-col`}
                        style={{
                            position: 'relative',
                            justifyContent: 'center',
                            alignItems: 'center',
                            // maxHeight: '500px'
                        }}
                    >
                        <Animation mouthOpen={mouthOpen} stop={isStopAnimation} isThinking={isThinking} thinkingBanner="thinking" hideThinkingText />
                    </div>
                    <div
                        className={`${(showChatHistory || showTopicList) ? 'flex sm:hidden' : 'hidden'} min-h-0 min-w-0 max-h-[min(92dvh,100dvh)] w-full max-w-full flex-grow flex-col overflow-hidden rounded-xl border border-gray-200 bg-white p-4 shadow-md`}
                        style={{
                            width: '90%',
                            margin: '10px 0px',
                            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
                        }}
                    >
                        {
                            showChatHistory && (
                                <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
                                    <h4
                                        className="mb-3 shrink-0 text-xl font-bold"
                                        style={GRADIENT_TITLE_STYLE}
                                    >
                                        Voice Chat
                                    </h4>
                                    <div className={SIDE_PANEL_INNER_CLASS}>
                                        <ChatHistory
                                            chatHistoryData={chatHistoryData}
                                            isThinking={isThinking}
                                            thinkingBanner="thinking"
                                            hideThinkingText
                                            newChatHistory={newChatHistory}
                                            scrollHeight="370px"
                                            fillContainer
                                        />
                                    </div>
                                </div>
                            )
                        }
                        {
                            showTopicList && (
                                <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
                                    <h4
                                        className="mb-2 shrink-0 text-xl font-bold"
                                        style={GRADIENT_TITLE_STYLE}
                                    >
                                        Topic
                                    </h4>
                                    <p className="mb-3 shrink-0 text-sm text-gray-500">
                                        Selecting a topic helps improve AI accuracy.
                                    </p>
                                    <div className={SIDE_PANEL_INNER_CLASS}>
                                        <TopicList
                                            selectedTopic={selectedTopic}
                                            topicList={topicList}
                                            handleClickSelectTopic={handleClickSelectTopic}
                                            scrollHeight="370px"
                                        />
                                    </div>
                                </div>
                            )
                        }
                    </div>
                    <div className="flex items-center justify-center">
                            <Button
                                onClick={onToggleListening}
                                className={`h-12 w-60 hover:opacity-90 shadow-md`}
                                aria-label={isRecording ? t("app.stopRecording") : t("app.startRecording")}
                                style={{
                                    width: '70px',
                                    height: '70px',
                                    margin: '10px',
                                    borderRadius: '50%',
                                    background: isRecording ? CHAT_AUDIO_PAUSE : CHAT_AUDIO_PLAY,
                                    transition: 'background 0.3s ease',
                                    transform: 'scale(1.05)',
                                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
                                }}
                            >
                                {isRecording ? (
                                    <>
                                        <Pause className="h-6 w-6 text-white-500 " style={{color: 'white'}}/>
                                    </>
                                ) : (
                                    <>
                                        <Play className="h-6 w-6 text-white-500" style={{color: 'white'}} />
                                    </>
                                )}
                            </Button>
                            <Button
                                onClick={handelSetMicOff}
                                className={`h-12 w-60 hover:opacity-80`}
                                aria-label={isMicOff ? t("app.stopRecording") : t("app.startRecording")}
                                style={{
                                    width: '70px',
                                    height: '70px',
                                    margin: '10px',
                                    borderRadius: '50%',
                                    background: isMicOff ? CHAT_MUTED_CORAL : CHAT_AUDIO_LISTEN_GRADIENT,
                                    transition: 'background 0.3s ease',
                                    transform: 'scale(1.05)',
                                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
                                }}
                            >
                                {isMicOff ? (
                                    <>
                                        <MicOff className="h-6 w-6 text-white-500" style={{color: 'white'}}/>
                                    </>
                                ) : (
                                    <>
                                        <Mic className="h-6 w-6 text-white-500 " style={{color: 'white'}}/>
                                    </>
                                )}
                            </Button>
                    </div>
                    <div style={{marginBottom: '1vh'}}>
                        <StatusMessage
                            isRecording={isRecording}
                            isThinking={isThinking}
                            thinkingBanner={thinkingBanner}
                        />
                        <GroundingFiles files={groundingFiles} onSelected={setSelectedFile} />
                    </div>
                </main>

                <footer className="shrink-0 px-3 py-3 text-right text-[12px] sm:mr-5 sm:py-4" style={{color: 'rgba(0, 0, 0, 0.5)'}}>
                    {t("app.footer")}
                </footer>

                <GroundingFileView groundingFile={selectedFile} onClosed={() => setSelectedFile(null)} />
            </div>
            {
                showChatHistory && (
                    <div
                        className={SIDE_PANEL_CLASS}
                        style={{
                            margin: '15px 15px 15px 0px',
                            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
                        }}
                    >
                        <h4
                            className="shrink-0 text-2xl font-bold md:text-3xl"
                            style={{
                                ...GRADIENT_TITLE_STYLE,
                                marginBottom: '16px',
                            }}
                        >
                            Voice Chat
                        </h4>
                        <div className={SIDE_PANEL_INNER_CLASS}>
                            <ChatHistory
                                chatHistoryData={chatHistoryData}
                                isThinking={isThinking}
                                thinkingBanner="thinking"
                                hideThinkingText
                                newChatHistory={newChatHistory}
                                scrollHeight="200px"
                                fillContainer
                            />
                        </div>
                    </div>
                )
            }
            {
                showTopicList && (
                    <div
                        className={SIDE_PANEL_CLASS}
                        style={{
                            margin: '15px 15px 15px 0px',
                            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
                        }}
                    >
                        <h4
                            className="shrink-0 text-2xl font-bold md:text-3xl"
                            style={{
                                ...GRADIENT_TITLE_STYLE,
                                marginBottom: '10px',
                            }}
                        >
                            Topic
                        </h4>
                        <div className="shrink-0 text-[14px] text-gray-500" style={{ marginBottom: '12px' }}>
                            Selecting a topic helps improve AI accuracy.
                        </div>
                        <div className={SIDE_PANEL_INNER_CLASS}>
                            <TopicList
                                selectedTopic={selectedTopic}
                                topicList={topicList}
                                handleClickSelectTopic={handleClickSelectTopic}
                                scrollHeight="200px"
                            />
                        </div>
                    </div>
                )
            }
        </div>
    );
}

function App() {
    return (
        <Router>
            <div className="App">
                <Navigation />
                <Routes>
                    <Route path="/callback" element={<SsoCallbackPage />} />
                    <Route path={SSO_POST_LOGOUT_PATH} element={<PostLogoutRedirectPage />} />
                    <Route path="/no-permission" element={<NoPermissionPage />} />
                    <Route path="/" element={<Navigate to="/voice" replace />} />
                    <Route element={<RequireAuth />}>
                        <Route path="/voice" element={<VoicePage />} />
                        <Route path="/chatbot" element={<ChatbotPage />} />
                        <Route path="/conversation-history" element={<ConversationHistoryPage />} />
                    </Route>
                </Routes>
            </div>
        </Router>
    );
}

export default App;